import { settings } from "../ui/settings";
import type { UnitDef } from "../data/types";

// Compact procedural audio layer (GDD §10). Pure WebAudio synthesis — no files,
// no CDNs. Everything is render/UI-side; the deterministic sim never calls this.
//
// Design goals from the polish brief:
//  - weapon reports differentiated per weapon class (laser / ballistic / plasma…)
//  - explosion booms scaled by size, with voice pooling + a dynamic-range cap so
//    a 100-unit fight sounds like a war, not white noise
//  - UI ticks, stall/overflow blips, victory/defeat stingers
//  - master volume + mute in settings; NO audio before the first user gesture.

export type WeaponSound =
  | "laser" // Vantage bolts / beams — bright zap
  | "ballistic" // fast kinetic bolt — thud/crack
  | "plasma" // arcing splash — whomp
  | "mortar" // high-arc artillery — deep lob
  | "sniper" // long rail — sharp crack
  | "flak"; // rapid AA — stutter pop

/** Map a unit/weapon def to a weapon-report voice. Pure, data-driven. */
export function weaponSoundClass(def: UnitDef): WeaponSound {
  const w = def.weapon;
  if (!w) return "ballistic";
  if (def.role === "sniper") return "sniper";
  if (def.role === "aa") return "flak";
  if (def.role === "artillery" || w.arc >= 2) return "mortar";
  if (w.projectileKind === "arc") return "plasma";
  // Straight bolts: Vantage laser vs generic kinetic.
  return def.faction === "feral" ? "ballistic" : "laser";
}

const MAX_VOICES = 18; // hard concurrency cap (dynamic-range protection)

class AudioEngine {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private limiter!: DynamicsCompressorNode;
  private noise!: AudioBuffer;
  private voices = 0;
  private started = false;
  // Per-category coalescing: min gap (s) between identical reports so a mass
  // volley collapses into a textured roll instead of stacking into mush.
  private lastAt: Record<string, number> = {};

  /** Create/resume the context. MUST be called from a user-gesture handler. */
  unlock(): void {
    if (!this.ctx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
      this.limiter = this.ctx.createDynamicsCompressor();
      // Aggressive brick-wall-ish limiter: caps the summed loudness of big fights.
      this.limiter.threshold.value = -14;
      this.limiter.knee.value = 6;
      this.limiter.ratio.value = 12;
      this.limiter.attack.value = 0.003;
      this.limiter.release.value = 0.18;
      this.master = this.ctx.createGain();
      this.master.gain.value = this.gain();
      this.master.connect(this.limiter);
      this.limiter.connect(this.ctx.destination);
      this.noise = this.makeNoise(this.ctx);
      this.started = true;
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    this.master.gain.value = this.gain();
  }

  get ready(): boolean {
    return this.started;
  }

  refreshVolume(): void {
    if (this.master) this.master.gain.value = this.gain();
  }

  private gain(): number {
    return settings.muted ? 0 : settings.sfxVolume;
  }

  private makeNoise(ctx: AudioContext): AudioBuffer {
    const len = ctx.sampleRate * 1;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    return buf;
  }

  private canPlay(cat: string, minGap: number): boolean {
    if (!this.ctx || !this.started || this.gain() <= 0) return false;
    if (this.voices >= MAX_VOICES) return false;
    const t = this.ctx.currentTime;
    if ((this.lastAt[cat] ?? -1) + minGap > t) return false;
    this.lastAt[cat] = t;
    return true;
  }

  private track(node: AudioScheduledSourceNode, stop: number): void {
    this.voices++;
    node.onended = () => {
      this.voices = Math.max(0, this.voices - 1);
    };
    node.stop(stop);
  }

  private env(dur: number, peak: number): GainNode {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    const t = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    g.connect(this.master);
    return g;
  }

  private noiseSrc(): AudioBufferSourceNode {
    const s = this.ctx!.createBufferSource();
    s.buffer = this.noise;
    s.loop = true;
    s.playbackRate.value = 0.6 + Math.random() * 0.8;
    return s;
  }

  // ---- Weapon reports -------------------------------------------------------

  weapon(cls: WeaponSound, faction: number, gain = 1): void {
    // Density-adaptive: as more shots overlap, each softens (limiter + this keep
    // a big battle textured, not white-noise). Small per-class gaps coalesce.
    const g = gain * (this.voices > 8 ? 0.5 : 1);
    switch (cls) {
      case "laser": this.laser(faction, g); break;
      case "ballistic": this.ballistic(g); break;
      case "plasma": this.plasma(g); break;
      case "mortar": this.mortar(g); break;
      case "sniper": this.sniper(g); break;
      case "flak": this.flak(g); break;
    }
  }

  private laser(faction: number, gain: number): void {
    if (!this.canPlay("laser", 0.04)) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = "sawtooth";
    const base = faction === 1 ? 720 : 900;
    o.frequency.setValueAtTime(base * (0.95 + Math.random() * 0.1), t);
    o.frequency.exponentialRampToValueAtTime(base * 0.35, t + 0.09);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(3200, t);
    lp.frequency.exponentialRampToValueAtTime(700, t + 0.1);
    const g = this.env(0.12, 0.16 * gain);
    o.connect(lp); lp.connect(g);
    o.start(t); this.track(o, t + 0.14);
  }

  private ballistic(gain: number): void {
    if (!this.canPlay("ball", 0.03)) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const n = this.noiseSrc();
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 1200 + Math.random() * 400;
    bp.Q.value = 0.8;
    const g = this.env(0.09, 0.22 * gain);
    n.connect(bp); bp.connect(g);
    n.start(t); this.track(n, t + 0.1);
    // low kick body
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.setValueAtTime(180, t);
    o.frequency.exponentialRampToValueAtTime(60, t + 0.08);
    const g2 = this.env(0.09, 0.16 * gain);
    o.connect(g2); o.start(t); this.track(o, t + 0.1);
  }

  private plasma(gain: number): void {
    if (!this.canPlay("plasma", 0.045)) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = "triangle";
    o.frequency.setValueAtTime(420, t);
    o.frequency.exponentialRampToValueAtTime(150, t + 0.13);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 1400;
    const g = this.env(0.16, 0.2 * gain);
    o.connect(lp); lp.connect(g);
    o.start(t); this.track(o, t + 0.18);
  }

  private mortar(gain: number): void {
    if (!this.canPlay("mortar", 0.06)) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.setValueAtTime(260, t);
    o.frequency.exponentialRampToValueAtTime(70, t + 0.2);
    const g = this.env(0.22, 0.24 * gain);
    o.connect(g); o.start(t); this.track(o, t + 0.24);
  }

  private sniper(gain: number): void {
    if (!this.canPlay("sniper", 0.05)) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const n = this.noiseSrc();
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 2600;
    const g = this.env(0.08, 0.28 * gain);
    n.connect(hp); hp.connect(g);
    n.start(t); this.track(n, t + 0.09);
    const o = ctx.createOscillator();
    o.type = "square";
    o.frequency.setValueAtTime(900, t);
    o.frequency.exponentialRampToValueAtTime(200, t + 0.06);
    const g2 = this.env(0.07, 0.14 * gain);
    o.connect(g2); o.start(t); this.track(o, t + 0.09);
  }

  private flak(gain: number): void {
    if (!this.canPlay("flak", 0.02)) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const n = this.noiseSrc();
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 2000;
    bp.Q.value = 1.4;
    const g = this.env(0.05, 0.16 * gain);
    n.connect(bp); bp.connect(g);
    n.start(t); this.track(n, t + 0.06);
  }

  // ---- Explosions -----------------------------------------------------------

  explosion(radius: number, big: boolean, gain = 1): void {
    const cat = big ? "boom-big" : "boom";
    if (!this.canPlay(cat, big ? 0.03 : 0.05)) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const size = Math.min(1.6, radius / 40);
    const dur = big ? 1.1 : 0.35 + size * 0.35;
    // Noise body through a downward-sweeping lowpass = the "whump".
    const n = this.noiseSrc();
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(big ? 1800 : 1200, t);
    lp.frequency.exponentialRampToValueAtTime(big ? 90 : 200, t + dur);
    const g = this.env(dur, (big ? 0.9 : 0.4 + size * 0.25) * gain);
    n.connect(lp); lp.connect(g);
    n.start(t); this.track(n, t + dur + 0.05);
    // Sub-bass sine drop for weight.
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.setValueAtTime(big ? 120 : 90, t);
    o.frequency.exponentialRampToValueAtTime(big ? 28 : 45, t + dur * 0.8);
    const g2 = this.env(dur * 0.9, (big ? 0.8 : 0.3 + size * 0.2) * gain);
    o.connect(g2); o.start(t); this.track(o, t + dur + 0.05);
  }

  // ---- UI + status ----------------------------------------------------------

  ui(kind: "click" | "place" | "select" | "deny"): void {
    if (!this.canPlay("ui-" + kind, 0.02)) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = kind === "deny" ? "sawtooth" : "square";
    const f = kind === "click" ? 660 : kind === "place" ? 520 : kind === "select" ? 880 : 200;
    o.frequency.setValueAtTime(f, t);
    if (kind === "place") o.frequency.exponentialRampToValueAtTime(720, t + 0.05);
    if (kind === "deny") o.frequency.exponentialRampToValueAtTime(120, t + 0.08);
    const g = this.env(kind === "deny" ? 0.12 : 0.05, 0.09);
    o.connect(g); o.start(t); this.track(o, t + 0.14);
  }

  warn(kind: "stall" | "over"): void {
    if (!this.canPlay("warn-" + kind, 0.5)) return; // slow — this is a heartbeat
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = "sine";
    const f = kind === "stall" ? 300 : 520;
    o.frequency.setValueAtTime(f, t);
    o.frequency.linearRampToValueAtTime(f * (kind === "stall" ? 0.8 : 1.15), t + 0.12);
    const g = this.env(0.16, 0.1);
    o.connect(g); o.start(t); this.track(o, t + 0.18);
  }

  stinger(win: boolean): void {
    if (!this.ctx || !this.started || this.gain() <= 0) return;
    const ctx = this.ctx!;
    const notes = win ? [392, 523, 659, 784] : [330, 294, 247, 196];
    notes.forEach((f, i) => {
      const t = ctx.currentTime + i * 0.16;
      const o = ctx.createOscillator();
      o.type = "triangle";
      o.frequency.setValueAtTime(f, t);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.22, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
      g.connect(this.master);
      o.connect(g); o.start(t); this.track(o, t + 0.55);
    });
  }
}

export const audio = new AudioEngine();

// Install a one-time unlock on the first user gesture anywhere (autoplay policy).
let unlockBound = false;
export function bindAudioUnlock(): void {
  if (unlockBound) return;
  unlockBound = true;
  const on = () => {
    audio.unlock();
    window.removeEventListener("pointerdown", on);
    window.removeEventListener("keydown", on);
  };
  window.addEventListener("pointerdown", on);
  window.addEventListener("keydown", on);
}
